import React from 'react'
import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { CommonBorderGroupProps } from './types'
import { BorderColorControl } from '../../controls/border-color-control'
import { BorderStyleControl } from '../../controls/border-style-control'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { BorderWidthControl } from '../../controls/border-width-control'
import { BorderRadiusControl } from '../../controls/border-radius-control'
import { BorderTypeControl } from '../../controls/border-type-control'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { BorderType } from 'common/types/styleTypes'

type EntityExtend<
  PropertyKey extends string,
  MobilePropertyKey extends string,
> = {
  [K in PropertyKey]?: Partial<BorderType> | undefined
} & {
  [K in MobilePropertyKey]?: Partial<BorderType> | undefined
}

type Update = (value: Partial<BorderType>) => void

export interface EntityBorderGroupProps<
  PropertyKey extends string,
  MobilePropertyKey extends string,
  Entity extends EntityExtend<PropertyKey, MobilePropertyKey>,
> extends Omit<CommonBorderGroupProps, 'update' | 'mobileUpdate'> {
  entity: Entity
  propertyKey: PropertyKey
  mobilePropertyKey?: MobilePropertyKey
}

export const EntityBorderGroup = <
  PropertyKey extends string,
  MobilePropertyKey extends string,
  Entity extends EntityExtend<PropertyKey, MobilePropertyKey>,
>({
  entity,
  propertyKey,
  mobilePropertyKey,
  calculateValuesHook,
}: EntityBorderGroupProps<PropertyKey, MobilePropertyKey, Entity>) => {
  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()

  const resultKey = (propertyKey || 'border') as PropertyKey
  const resultMobileKey = (mobilePropertyKey ||
    'mobileBorder') as MobilePropertyKey

  const border = entity[resultKey]
  const mobileBorder = resultMobileKey && entity[resultMobileKey]

  const update = updateProp(resultKey) as Update
  const mobileUpdate =
    resultMobileKey && (updateProp(resultMobileKey) as Update)

  const { calculatedBorder } = calculateValuesHook
    ? calculateValuesHook({
        border,
        mobileBorder,
      })
    : {
        calculatedBorder: getMobileOrDesktop(border, mobileBorder, isMobile),
      }

  return (
    <>
      <GroupTitle isMobile={isMobile}>
        settings_styles.border_style.group_title
      </GroupTitle>
      <BorderTypeControl
        label=""
        border={calculatedBorder}
        update={update}
        mobileUpdate={mobileUpdate}
      />
      <BorderStyleControl
        border={calculatedBorder}
        update={update}
        mobileUpdate={mobileUpdate}
      />
      <BorderWidthControl
        border={calculatedBorder}
        update={update}
        mobileUpdate={mobileUpdate}
      />

      <BorderRadiusControl
        border={calculatedBorder}
        update={update}
        mobileUpdate={mobileUpdate}
      />
      <BorderColorControl
        border={calculatedBorder}
        update={update}
        mobileUpdate={mobileUpdate}
      />
    </>
  )
}
